import React, { useEffect, useState } from "react";
import { Container, Row, Col, Button } from "reactstrap";
import { motion } from "framer-motion";
import axios from "axios";
import { fetchSignedUrl } from "../../utils/fileUtils";

const HomePage = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const [bannerKey, setBannerKey] = useState(null);
  const [bannerUrl, setBannerUrl] = useState(null);
  const [bannerLoaded, setBannerLoaded] = useState(false);

  const toggleModal = () => setModalOpen(!modalOpen);

  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_COLLEGE_ERP_API}layout-config`, {
        params: { branch: "Kyts" },
      })
      .then((res) => {
        const key = res.data?.data?.bannerKey;
        setBannerKey(key ?? null);
      })
      .catch(() => {
        setBannerKey(null);
      });
  }, []);

  useEffect(() => {
    if (!bannerKey) return;

    const loadBanner = async () => {
      const url = await fetchSignedUrl(bannerKey);
      setBannerUrl(url);
    };

    loadBanner();
  }, [bannerKey]);

  return (
    <section className="modern-hero-section">
      {/* Scope-contained premium Wix/Hostinger global layout overrides */}
      <style>{`
        :root {
          --brand-dark: #0f172a;
          --brand-accent: #6366f1;
          --brand-accent-glow: rgba(99, 102, 241, 0.15);
          --text-slate: #475569;
          --radius-premium: 24px;
          --transition-fluid: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .modern-hero-section {
          position: relative;
          padding: 160px 0 100px 0;
          background: radial-gradient(circle at 90% 10%, var(--brand-accent-glow) 0%, transparent 45%),
                      radial-gradient(circle at 10% 80%, rgba(99, 102, 241, 0.05) 0%, transparent 35%),
                      #ffffff;
          overflow: hidden;
        }

        /* Ambient Glow Decorators */
        .ambient-glow {
          position: absolute;
          width: 400px;
          height: 400px;
          background: linear-gradient(45deg, #6366f1, #a855f7);
          filter: blur(120px);
          opacity: 0.12;
          top: 15%;
          right: -100px;
          border-radius: 50%;
          pointer-events: none;
        }

        /* Left Side Content */
        .modern-pill {
          display: inline-flex;
          align-items: center;
          padding: 6px 14px;
          background: rgba(99, 102, 241, 0.08);
          border: 1px solid rgba(99, 102, 241, 0.15);
          border-radius: 100px;
          color: var(--brand-accent);
          font-size: 0.85rem;
          font-weight: 600;
          letter-spacing: 0.3px;
          margin-bottom: 24px;
        }

        .modern-heading {
          font-size: clamp(2.5rem, 5vw, 3.75rem);
          font-weight: 800;
          line-height: 1.15;
          color: var(--brand-dark);
          letter-spacing: -1.5px;
          margin-bottom: 20px;
        }

        .gradient-text {
          background: linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
        }

        .modern-hero-text {
          font-size: 1.125rem;
          line-height: 1.75;
          color: var(--text-slate);
          max-width: 520px;
          margin-bottom: 36px;
        }

        /* Premium Buttons */
        .cta-container {
          display: flex;
          align-items: center;
          gap: 16px;
          flex-wrap: wrap;
        }

        .btn-premium-primary {
          background: var(--brand-dark) !important;
          color: #ffffff !important;
          font-weight: 600 !important;
          font-size: 1rem !important;
          padding: 14px 32px !important;
          border-radius: 12px !important;
          border: 1px solid var(--brand-dark) !important;
          box-shadow: 0 4px 14px rgba(15, 23, 42, 0.15);
          transition: var(--transition-fluid) !important;
        }
        .btn-premium-primary:hover {
          background: var(--brand-accent) !important;
          border-color: var(--brand-accent) !important;
          box-shadow: 0 6px 20px rgba(99, 102, 241, 0.3);
          transform: translateY(-2px);
        }

        .btn-premium-outline {
          background: transparent !important;
          color: var(--brand-dark) !important;
          font-weight: 600 !important;
          font-size: 1rem !important;
          padding: 14px 32px !important;
          border-radius: 12px !important;
          border: 1px solid rgba(15, 23, 42, 0.15) !important;
          transition: var(--transition-fluid) !important;
        }
        .btn-premium-outline:hover {
          background: rgba(15, 23, 42, 0.03) !important;
          border-color: var(--brand-dark) !important;
          transform: translateY(-2px);
        }

        /* Right Side Media Frame */
        .media-showcase-container {
          position: relative;
          display: flex;
          justify-content: center;
          align-items: center;
        }

        .modern-image-frame {
          width: 100%;
          max-width: 540px;
          aspect-ratio: 4 / 3;
          border-radius: var(--radius-premium);
          background: #f8fafc;
          border: 1px solid rgba(0, 0, 0, 0.05);
          box-shadow: 0 20px 40px rgba(15, 23, 42, 0.06);
          overflow: hidden;
          position: relative;
        }

        .hero-showcase-img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          opacity: 0;
          transition: opacity 0.5s ease-in-out;
        }

        .hero-showcase-img.image-is-loaded {
          opacity: 1;
        }

        /* Smooth Skeleton Loader Pulse */
        .hero-skeleton-pulse {
          position: absolute;
          inset: 0;
          background: linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%);
          background-size: 200% 100%;
          animation: skeleton-wave 1.6s infinite linear;
        }

        @keyframes skeleton-wave {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }

        @media (max-width: 991.98px) {
          .modern-hero-section {
            padding: 120px 0 60px 0;
            text-align: center;
          }
          .modern-hero-text {
            margin-left: auto;
            margin-right: auto;
          }
          .cta-container {
            justify-content: center;
          }
          .media-showcase-container {
            margin-top: 60px;
          }
        }
      `}</style>

      {/* Decorative Blur Background Element */}
      <div className="ambient-glow" />

      <Container>
        <Row className="align-items-center">
          {/* LEFT CONTENT BLOCK */}
          <Col lg="6">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            >
              <div className="modern-pill">
                <span>Modern Library Platform</span>
              </div>

              <h1 className="modern-heading">
                Reading made <br />
                <span className="gradient-text">beautifully simple</span>
              </h1>

              <p className="modern-hero-text">
                Discover, rent, and enjoy books with flexible monthly plans.
                Designed for readers who appreciate clarity, comfort, and value.
              </p>

              <div className="cta-container">
                <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                  <Button className="btn-premium-primary" onClick={toggleModal}>
                    Start Reading
                  </Button>
                </motion.div>

                <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                  <Button className="btn-premium-outline" href="#pricing">
                    View Plans
                  </Button>
                </motion.div>
              </div>
            </motion.div>
          </Col>

          {/* RIGHT MEDIA BLOCK */}
          <Col lg="6" className="media-showcase-container">
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 1, ease: [0.16, 1, 0.3, 1], delay: 0.1 }}
              className="modern-image-frame"
            >
              {/* 1️⃣ UNKNOWN / FETCHING STATE → SKELETON */}
              {bannerKey === undefined && <div className="hero-skeleton-pulse" />}

              {/* 2️⃣ DYNAMIC BANNER RENDER */}
              {bannerKey && bannerUrl && (
                <img
                  src={bannerUrl}
                  alt="Library Exhibition Showcase"
                  className={`hero-showcase-img ${bannerLoaded ? "image-is-loaded" : ""}`}
                  onLoad={() => setBannerLoaded(true)}
                />
              )}

              {/* 3️⃣ ABSOLUTE FALLBACK / DEFAULT STATE */}
              {bannerKey === null && (
                <img
                  src="/images/img1.jpeg"
                  alt="Library Exhibition Standard Showcase"
                  className="hero-showcase-img image-is-loaded"
                />
              )}
            </motion.div>
          </Col>
        </Row>
      </Container>
    </section>
  );
};

export default HomePage;